import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, downloadUrl } from "../lib/api";
import { fmtTL, fmtDate } from "../lib/format";
import { FileDown, ChevronDown, ChevronRight } from "lucide-react";
import DailyBudget from "../components/DailyBudget";
import { readJson } from "../lib/storage";

const monthName = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const names = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  return `${names[parseInt(m, 10) - 1] || m} ${y}`;
};
const savedBudgetMonth = () => readJson("dailyBudget", {}).month;

export default function Budget() {
  const [data, setData] = useState(null);
  const [openProjects, setOpenProjects] = useState({});
  const [exportMonth, setExportMonth] = useState(() => savedBudgetMonth() || new Date().toISOString().slice(0, 7));
  useEffect(() => {
    const sync = () => { const m = savedBudgetMonth(); if (m) setExportMonth(m); };
    const t = setInterval(sync, 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const load = () => http.get("/invoices/budget").then((r) => setData(r.data));
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  const g = data.grand;

  return (
    <div className="space-y-6" data-testid="page-budget">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bütçe & Gelir/Gider Özeti</h1>
          <p className="text-sm text-slate-500 mt-1">Fatura bazlı aylık gelir-gider özetin ve proje kırılımı.</p>
        </div>
        <a href={downloadUrl(`/invoices/budget/export?month=${encodeURIComponent(exportMonth)}&year=${exportMonth.slice(0, 4)}`)} target="_blank" rel="noreferrer" data-testid="budget-export"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-emerald-700 text-emerald-800 text-sm font-semibold hover:bg-emerald-50 transition-colors">
          <FileDown className="w-4 h-4" /> Excel İndir (Tüm Tablolar)
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi" data-testid="kpi-total-income">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Gelir</div>
          <div className="mt-2 text-2xl font-bold font-mono tracking-tight text-emerald-700">{fmtTL(g.income)}</div>
        </div>
        <div className="kpi" data-testid="kpi-total-expense">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Gider</div>
          <div className="mt-2 text-2xl font-bold font-mono tracking-tight text-red-700">{fmtTL(g.expense)}</div>
        </div>
        <div className="kpi" data-testid="kpi-total-net">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Net (Gelir − Gider)</div>
          <div className={`mt-2 text-2xl font-bold font-mono tracking-tight ${g.net >= 0 ? "text-slate-900" : "text-red-700"}`}>{fmtTL(g.net)}</div>
        </div>
      </div>

      <DailyBudget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold">Aylık Gelir / Gider</div>
            <div className="text-xs text-slate-500">Fatura tarihi bazında</div>
          </div>
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Ay</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Gelir</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Gider</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-amber-700 font-semibold">Çek (Dahil)</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-sky-700 font-semibold">Kart (Dahil)</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Net</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Açık Gider</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => (
                <tr key={m.month} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-xs">{monthName(m.month)}</td>
                  <td className="numeric-cell px-3 py-2 text-xs text-emerald-700">{fmtTL(m.income)}</td>
                  <td className="numeric-cell px-3 py-2 text-xs text-red-700">{fmtTL(m.expense)}</td>
                  <td className="numeric-cell px-3 py-2 text-xs text-amber-700">{fmtTL(m.checks || 0)}</td>
                  <td className="numeric-cell px-3 py-2 text-xs text-sky-700">{fmtTL(m.credit_cards || 0)}</td>
                  <td className={`numeric-cell px-3 py-2 text-xs font-semibold ${m.net >= 0 ? "text-slate-900" : "text-red-700"}`}>{fmtTL(m.net)}</td>
                  <td className="numeric-cell px-3 py-2 text-xs text-amber-700">{fmtTL(m.expense_open)}</td>
                </tr>
              ))}
              {data.months.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-500">Kayıt yok</td></tr>}
            </tbody>
            <tfoot className="bg-slate-100">
              <tr>
                <td className="px-3 py-3 text-xs font-bold uppercase tracking-wider">Genel Toplam</td>
                <td className="numeric-cell px-3 py-3 text-sm font-bold text-emerald-700" data-testid="budget-grand-income">{fmtTL(g.income)}</td>
                <td className="numeric-cell px-3 py-3 text-sm font-bold text-red-700" data-testid="budget-grand-expense">{fmtTL(g.expense)}</td>
                <td className="numeric-cell px-3 py-3 text-sm font-bold text-amber-700" data-testid="budget-grand-checks">{fmtTL(g.checks_total || 0)}</td>
                <td className="numeric-cell px-3 py-3 text-sm font-bold text-sky-700" data-testid="budget-grand-cards">{fmtTL(g.credit_cards_total || 0)}</td>
                <td className="numeric-cell px-3 py-3 text-sm font-bold" data-testid="budget-grand-net">{fmtTL(g.net)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold">Proje Bazlı Gelir / Gider</div>
            <div className="text-xs text-slate-500">Fatura kalemleri toplamı</div>
          </div>
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Proje</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Fatura</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Gelir</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Gider</th>
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.projects.map((p) => {
                const open = !!openProjects[p.project_name];
                return (
                  <Fragment key={p.project_name}>
                    <tr onClick={() => setOpenProjects((o) => ({ ...o, [p.project_name]: !o[p.project_name] }))} className={`border-t border-slate-100 cursor-pointer select-none hover:bg-slate-50 ${open ? "bg-amber-50" : ""}`} data-testid={`budget-project-row-${p.project_name}`} title="Faturaları görmek için tıklayın">
                      <td className="px-3 py-2 text-xs font-medium"><span className="inline-flex items-center gap-1.5">{open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}{p.project_name}</span></td>
                      <td className="numeric-cell px-3 py-2 text-xs">{p.count}</td>
                      <td className="numeric-cell px-3 py-2 text-xs text-emerald-700">{fmtTL(p.income)}</td>
                      <td className="numeric-cell px-3 py-2 text-xs text-red-700">{fmtTL(p.expense)}</td>
                      <td className={`numeric-cell px-3 py-2 text-xs font-semibold ${p.net >= 0 ? "text-slate-900" : "text-red-700"}`}>{fmtTL(p.net)}</td>
                    </tr>
                    {open && (
                      <tr className="bg-sky-50/40" data-testid={`budget-project-detail-${p.project_name}`}>
                        <td colSpan={5} className="px-4 py-2">
                          <table className="w-full text-[11px]">
                            <thead><tr className="text-[9px] uppercase tracking-wider text-slate-500"><th className="text-left px-2 py-1">Tarih</th><th className="text-left px-2 py-1">Belge No</th><th className="text-left px-2 py-1">Cari</th><th className="text-left px-2 py-1">Kategori</th><th className="text-right px-2 py-1">Gelir</th><th className="text-right px-2 py-1">Gider</th><th className="px-2 py-1">Durum</th></tr></thead>
                            <tbody>
                              {(p.invoices || []).map((inv) => (
                                <tr key={inv.id} className="border-t border-slate-100">
                                  <td className="px-2 py-1 whitespace-nowrap">{fmtDate(inv.invoice_date)}</td>
                                  <td className="px-2 py-1"><Link to={`/veri-girisi?bolum=fatura&fatura=${inv.id}&don=${encodeURIComponent("/butce")}`} className="font-mono text-sky-700 hover:underline" data-testid={`budget-inv-link-${inv.id}`} title="Kayda git (ESC ile geri dön)">{inv.invoice_no}</Link></td>
                                  <td className="px-2 py-1">{inv.vendor}</td>
                                  <td className="px-2 py-1 text-slate-500">{inv.category}</td>
                                  <td className="numeric-cell px-2 py-1 text-emerald-700">{inv.direction === "income" ? fmtTL(inv.amount) : "-"}</td>
                                  <td className="numeric-cell px-2 py-1 text-red-700">{inv.direction !== "income" ? fmtTL(inv.amount) : "-"}</td>
                                  <td className="px-2 py-1 text-center"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${inv.paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{inv.paid ? "Ödendi" : "Açık"}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {data.projects.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-slate-500">Kayıt yok</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-5 py-3" data-testid="budget-checks-strip">
        <span className="px-2 py-0.5 rounded bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-wider">Çek</span>
        <span className="text-sm text-amber-900">
          Verilen çekler toplamı <span className="font-mono font-bold">{fmtTL(g.checks_total || 0)}</span> — yukarıdaki gider toplamlarına dahildir.
        </span>
      </div>
    </div>
  );
}
